// Módulo "Saída de linha": SKUs em acompanhamento para possível retirada do mix.
// Cada item tem um plano (prazo + metas do último mês), lançamentos mensais
// (faturamento, margem de contribuição, clientes positivados) e um histórico de
// decisões (prorrogar / desativar / voltar ao mix). Permite dados retroativos.
import { supabase } from './supabase';
import { ultimoMesLiberado } from '../shared/fechamento';

export type DiscStatus = 'em_acompanhamento' | 'desativado' | 'voltou_mix';

export const DISC_STATUS_LABELS: Record<DiscStatus, string> = {
  em_acompanhamento: 'Em acompanhamento',
  desativado: 'SKU desativado',
  voltou_mix: 'Voltou para o mix',
};

export interface Discontinuation {
  id: string;
  orgId: string;
  brandId: string | null;
  brandName: string | null;
  name: string;
  codigoInterno: string | null;
  photoUrl: string | null;
  motivo: string | null;
  responsavelId: string | null;
  responsavelNome: string | null;
  startDate: string; // 'YYYY-MM-DD'
  planMonths: number;
  coberturaTempo: string | null;
  coberturaValor: number | null;
  metaFaturamento: number | null;
  metaMargem: number | null;
  metaClientes: number | null;
  metaNfd: number | null;
  comentarios: string | null;
  status: DiscStatus;
  outcomeComment: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface DiscEntry {
  id: string;
  discontinuationId: string;
  refMonth: string; // 'YYYY-MM-01'
  faturamento: number | null;
  margemContribuicao: number | null;
  clientesPositivados: number | null;
  nfd: number | null;
  comentarios: string | null;
  byName: string | null;
  createdAt: string;
}

export type DiscEventKind = 'prorrogacao' | 'desativar' | 'voltar_mix' | 'nota';

export interface DiscEvent {
  id: string;
  discontinuationId: string;
  kind: DiscEventKind;
  monthsAdded: number | null;
  metaFaturamento: number | null;
  metaMargem: number | null;
  metaClientes: number | null;
  metaNfd: number | null;
  comment: string | null;
  byName: string | null;
  createdAt: string;
}

// ---- helpers de data --------------------------------------------------------
/** Primeiro dia do mês (YYYY-MM-01) a partir de uma data qualquer. */
export function monthKey(d: Date | string): string {
  const dt = typeof d === 'string' ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')) : d;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Label curto de mês, ex.: "out/25". */
export function monthLabel(ym: string): string {
  const dt = new Date(ym + 'T00:00:00');
  const mes = dt.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${mes}/${String(dt.getFullYear()).slice(2)}`;
}

/** Lista de meses do plano (início .. início + planMonths-1), como YYYY-MM-01. */
export function planMonthsList(startDate: string, planMonths: number): string[] {
  const start = new Date(monthKey(startDate) + 'T00:00:00');
  const out: string[] = [];
  for (let i = 0; i < Math.max(1, planMonths); i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    out.push(monthKey(d));
  }
  return out;
}

// ---- mapeadores -------------------------------------------------------------
function mapDisc(r: Record<string, unknown>): Discontinuation {
  const brand = r.brands as { name?: string } | { name?: string }[] | null;
  const brandName = Array.isArray(brand) ? brand[0]?.name ?? null : brand?.name ?? null;
  return {
    id: r.id as string,
    orgId: r.org_id as string,
    brandId: (r.brand_id as string) ?? null,
    brandName,
    name: r.name as string,
    codigoInterno: (r.codigo_interno as string) ?? null,
    photoUrl: (r.photo_url as string) ?? null,
    motivo: (r.motivo as string) ?? null,
    responsavelId: (r.responsavel_id as string) ?? null,
    responsavelNome: (r.responsavel_nome as string) ?? null,
    startDate: r.start_date as string,
    planMonths: (r.plan_months as number) ?? 6,
    coberturaTempo: (r.cobertura_tempo as string) ?? null,
    coberturaValor: (r.cobertura_valor as number) ?? null,
    metaFaturamento: (r.meta_faturamento as number) ?? null,
    metaMargem: (r.meta_margem as number) ?? null,
    metaClientes: (r.meta_clientes as number) ?? null,
    metaNfd: (r.meta_nfd as number) ?? null,
    comentarios: (r.comentarios as string) ?? null,
    status: (r.status as DiscStatus) ?? 'em_acompanhamento',
    outcomeComment: (r.outcome_comment as string) ?? null,
    createdByName: (r.created_by_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}

function mapEntry(r: Record<string, unknown>): DiscEntry {
  return {
    id: r.id as string,
    discontinuationId: r.discontinuation_id as string,
    refMonth: r.ref_month as string,
    faturamento: (r.faturamento as number) ?? null,
    margemContribuicao: (r.margem_contribuicao as number) ?? null,
    clientesPositivados: (r.clientes_positivados as number) ?? null,
    nfd: (r.nfd as number) ?? null,
    comentarios: (r.comentarios as string) ?? null,
    byName: (r.by_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}

function mapEvent(r: Record<string, unknown>): DiscEvent {
  return {
    id: r.id as string,
    discontinuationId: r.discontinuation_id as string,
    kind: r.kind as DiscEventKind,
    monthsAdded: (r.months_added as number) ?? null,
    metaFaturamento: (r.meta_faturamento as number) ?? null,
    metaMargem: (r.meta_margem as number) ?? null,
    metaClientes: (r.meta_clientes as number) ?? null,
    metaNfd: (r.meta_nfd as number) ?? null,
    comment: (r.comment as string) ?? null,
    byName: (r.by_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}

const DISC_COLS =
  'id, org_id, brand_id, name, codigo_interno, photo_url, motivo, responsavel_id, responsavel_nome, start_date, plan_months, cobertura_tempo, cobertura_valor, meta_faturamento, meta_margem, meta_clientes, meta_nfd, comentarios, status, outcome_comment, created_by_name, created_at, brands(name)';

// ---- CRUD -------------------------------------------------------------------
export async function loadDiscontinuations(orgId: string): Promise<Discontinuation[]> {
  const { data } = await supabase
    .from('discontinuations')
    .select(DISC_COLS)
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map(mapDisc);
}

export async function loadDiscontinuation(id: string): Promise<Discontinuation | null> {
  const { data } = await supabase.from('discontinuations').select(DISC_COLS).eq('id', id).maybeSingle();
  return data ? mapDisc(data as Record<string, unknown>) : null;
}

export async function createDiscontinuation(params: {
  orgId: string;
  brandId: string | null;
  name: string;
  codigoInterno?: string | null;
  photoUrl?: string | null;
  motivo?: string | null;
  responsavelId?: string | null;
  responsavelNome?: string | null;
  startDate?: string | null;
  planMonths?: number;
  coberturaTempo?: string | null;
  coberturaValor?: number | null;
  metaFaturamento?: number | null;
  metaMargem?: number | null;
  metaClientes?: number | null;
  metaNfd?: number | null;
  comentarios?: string | null;
  createdById?: string | null;
  createdByName?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const { data, error } = await supabase
    .from('discontinuations')
    .insert({
      org_id: params.orgId,
      brand_id: params.brandId,
      name: params.name,
      codigo_interno: params.codigoInterno ?? null,
      photo_url: params.photoUrl ?? null,
      motivo: params.motivo ?? null,
      responsavel_id: params.responsavelId ?? null,
      responsavel_nome: params.responsavelNome ?? null,
      start_date: params.startDate || new Date().toISOString().slice(0, 10),
      plan_months: params.planMonths ?? 6,
      cobertura_tempo: params.coberturaTempo ?? null,
      cobertura_valor: params.coberturaValor ?? null,
      meta_faturamento: params.metaFaturamento ?? null,
      meta_margem: params.metaMargem ?? null,
      meta_clientes: params.metaClientes ?? null,
      meta_nfd: params.metaNfd ?? null,
      comentarios: params.comentarios ?? null,
      created_by: params.createdById ?? null,
      created_by_name: params.createdByName ?? null,
    })
    .select('id')
    .single();
  return { error: error?.message, id: (data as { id: string } | null)?.id };
}

export async function updateDiscontinuation(
  id: string,
  patch: Record<string, unknown>,
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('discontinuations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  return { error: error?.message };
}

export async function deleteDiscontinuation(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuations').delete().eq('id', id);
  return { error: error?.message };
}

// ---- lançamentos mensais ----------------------------------------------------
export async function loadEntries(discontinuationId: string): Promise<DiscEntry[]> {
  const { data } = await supabase
    .from('discontinuation_entries')
    .select('*')
    .eq('discontinuation_id', discontinuationId)
    .order('ref_month', { ascending: true });
  return ((data ?? []) as Record<string, unknown>[]).map(mapEntry);
}

/** Cria ou atualiza o lançamento de um mês (um por mês de referência). */
export async function upsertEntry(params: {
  orgId: string;
  discontinuationId: string;
  refMonth: string; // YYYY-MM-01
  faturamento?: number | null;
  margemContribuicao?: number | null;
  clientesPositivados?: number | null;
  nfd?: number | null;
  comentarios?: string | null;
  byId?: string | null;
  byName?: string | null;
}): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuation_entries').upsert(
    {
      org_id: params.orgId,
      discontinuation_id: params.discontinuationId,
      ref_month: params.refMonth,
      faturamento: params.faturamento ?? null,
      margem_contribuicao: params.margemContribuicao ?? null,
      clientes_positivados: params.clientesPositivados ?? null,
      nfd: params.nfd ?? null,
      comentarios: params.comentarios ?? null,
      by_id: params.byId ?? null,
      by_name: params.byName ?? null,
    },
    { onConflict: 'discontinuation_id,ref_month' },
  );
  return { error: error?.message };
}

export async function deleteEntry(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuation_entries').delete().eq('id', id);
  return { error: error?.message };
}

// ---- decisões / histórico ---------------------------------------------------
export async function loadEvents(discontinuationId: string): Promise<DiscEvent[]> {
  const { data } = await supabase
    .from('discontinuation_events')
    .select('*')
    .eq('discontinuation_id', discontinuationId)
    .order('created_at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map(mapEvent);
}

export async function addEvent(params: {
  orgId: string;
  discontinuationId: string;
  kind: DiscEventKind;
  monthsAdded?: number | null;
  metaFaturamento?: number | null;
  metaMargem?: number | null;
  metaClientes?: number | null;
  metaNfd?: number | null;
  comment?: string | null;
  byId?: string | null;
  byName?: string | null;
}): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuation_events').insert({
    org_id: params.orgId,
    discontinuation_id: params.discontinuationId,
    kind: params.kind,
    months_added: params.monthsAdded ?? null,
    meta_faturamento: params.metaFaturamento ?? null,
    meta_margem: params.metaMargem ?? null,
    meta_clientes: params.metaClientes ?? null,
    meta_nfd: params.metaNfd ?? null,
    comment: params.comment ?? null,
    by_id: params.byId ?? null,
    by_name: params.byName ?? null,
  });
  return { error: error?.message };
}

/**
 * Aplica uma decisão e atualiza o item: prorrogação soma meses (e pode trocar as
 * metas), desativar / voltar ao mix mudam o status e guardam a tratativa. Mantém
 * o histórico via addEvent.
 */
export async function applyDecision(params: {
  disc: Discontinuation;
  kind: DiscEventKind;
  monthsAdded?: number | null;
  metaFaturamento?: number | null;
  metaMargem?: number | null;
  metaClientes?: number | null;
  metaNfd?: number | null;
  comment?: string | null;
  byId?: string | null;
  byName?: string | null;
}): Promise<{ error?: string }> {
  const { disc } = params;
  const ev = await addEvent({
    orgId: disc.orgId,
    discontinuationId: disc.id,
    kind: params.kind,
    monthsAdded: params.monthsAdded ?? null,
    metaFaturamento: params.metaFaturamento ?? null,
    metaMargem: params.metaMargem ?? null,
    metaClientes: params.metaClientes ?? null,
    metaNfd: params.metaNfd ?? null,
    comment: params.comment ?? null,
    byId: params.byId,
    byName: params.byName,
  });
  if (ev.error) return ev;

  const patch: Record<string, unknown> = {};
  if (params.kind === 'prorrogacao') {
    patch.plan_months = disc.planMonths + (params.monthsAdded ?? 0);
    if (params.metaFaturamento != null) patch.meta_faturamento = params.metaFaturamento;
    if (params.metaMargem != null) patch.meta_margem = params.metaMargem;
    if (params.metaClientes != null) patch.meta_clientes = params.metaClientes;
    if (params.metaNfd != null) patch.meta_nfd = params.metaNfd;
    if (params.comment) patch.outcome_comment = params.comment;
  } else if (params.kind === 'desativar') {
    patch.status = 'desativado';
    patch.outcome_comment = params.comment ?? disc.outcomeComment ?? null;
  } else if (params.kind === 'voltar_mix') {
    patch.status = 'voltou_mix';
    patch.outcome_comment = params.comment ?? disc.outcomeComment ?? null;
  }
  if (Object.keys(patch).length === 0) return {};
  return updateDiscontinuation(disc.id, patch);
}

// ---- comentários / histórico de decisões ------------------------------------
export interface DiscComment {
  id: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export async function loadComments(discontinuationId: string): Promise<DiscComment[]> {
  const { data } = await supabase
    .from('discontinuation_comments')
    .select('id, author_id, author_name, body, created_at')
    .eq('discontinuation_id', discontinuationId)
    .order('created_at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as string,
    authorId: (r.author_id as string) ?? null,
    authorName: (r.author_name as string) ?? null,
    body: r.body as string,
    createdAt: r.created_at as string,
  }));
}

export async function addComment(params: {
  orgId: string;
  discontinuationId: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
}): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuation_comments').insert({
    org_id: params.orgId,
    discontinuation_id: params.discontinuationId,
    author_id: params.authorId,
    author_name: params.authorName,
    body: params.body,
  });
  return { error: error?.message };
}

export async function deleteComment(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('discontinuation_comments').delete().eq('id', id);
  return { error: error?.message };
}

/**
 * Itens ativos que ainda não têm o lançamento do último mês JÁ FECHADO (liberado a partir do dia 10 do
 * mês seguinte) — usado para lembrar quem precisa preencher. O mês em andamento não é cobrado.
 */
export async function pendingThisMonth(orgId: string): Promise<Discontinuation[]> {
  const discs = (await loadDiscontinuations(orgId)).filter((d) => d.status === 'em_acompanhamento');
  const thisMonth = ultimoMesLiberado();
  const out: Discontinuation[] = [];
  for (const d of discs) {
    const entries = await loadEntries(d.id);
    const months = planMonthsList(d.startDate, d.planMonths);
    // só lembra se o mês atual faz parte do plano e ainda não foi lançado
    if (months.includes(thisMonth) && !entries.some((e) => e.refMonth === thisMonth)) out.push(d);
  }
  return out;
}
